import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { ConnectButton } from '@mysten/dapp-kit';
import './LandingPage.css';
import { getProtocolTotals } from '../utils/supabaseManager';

// ═══════════════════════════════════════════════════════════════════════════
// LANDING PAGE - Scroll sections with animations
// Inspired by Sui Rewards, DeepBook, Jackson.io
// Updated: Blockchain-level messaging, Sanitation Department of Sui
// Updated: Active buttons, social links placeholder
// ═══════════════════════════════════════════════════════════════════════════

function LandingPage({ onEnterApp, onGetStarted, onConnectWallet }) {
  const { scrollYProgress } = useScroll();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showBuildModal, setShowBuildModal] = useState(false);

  // Use whichever prop is provided
  const handleEnterApp = onEnterApp || onGetStarted || onConnectWallet;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="landing-page">
      <FloatingParticles />
      
      {/* Sticky Header */}
      <Header isScrolled={isScrolled} onEnterApp={handleEnterApp} />

      {/* Hero Section */}
      <HeroSection onEnterApp={handleEnterApp} />

      {/* Sanitation Department Section */}
      <SanitationDeptSection onEnterApp={handleEnterApp} />

      {/* How It Works */}
      <HowItWorksSection />

      {/* 🛡️ Security Section - NEW! */}
      <SecuritySection onEnterApp={handleEnterApp} />

      {/* Benefits */}
      <BenefitsSection />

      {/* Tokenomics */}
      <TokenomicsSection />

      {/* Toilet Mode Partnership */}
      <ToiletModeSection />

      {/* Stats Dashboard */}
      <StatsSection />

      {/* FAQ Section */}
      <FAQSection />

      {/* Roadmap Section */}
      <RoadmapSection />

      {/* CTA Section */}
      <CTASection onEnterApp={handleEnterApp} />

      {/* Footer */}
      <Footer 
        onOpenProcessModal={() => setShowProcessModal(true)}
        onOpenBuildModal={() => setShowBuildModal(true)}
      />

      {/* Modals */}
      <AnimatePresence>
        {showProcessModal && (
          <ProcessModal onClose={() => setShowProcessModal(false)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBuildModal && (
          <BuildModal onClose={() => setShowBuildModal(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HEADER
// ═══════════════════════════════════════════════════════════════════════════

function Header({ isScrolled, onEnterApp }) {
  return (
    <motion.header
      className={`landing-header ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 100 }}
    >
      <div className="header-content">
        <motion.img
          src="/images/distil-logo-animated.svg"
          alt="DISTIL"
          className="header-logo-img"
          whileHover={{ scale: 1.05 }}
          style={{ height: '60px', width: 'auto' }}
        />
        
        <nav className="header-nav">
          <a href="#how-it-works">How It Works</a>
          <a href="#benefits">Benefits</a>
          <a href="#stats">Stats</a>
          <motion.button
            className="launch-app-btn"
            onClick={onEnterApp}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Launch App
          </motion.button>
        </nav>
      </div>
    </motion.header>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HERO SECTION - UPDATED WITH BLOCKCHAIN-LEVEL MESSAGING
// ═══════════════════════════════════════════════════════════════════════════

function HeroSection({ onEnterApp }) {
  return (
    <section className="hero-section">
      <div className="hero-content">
        <motion.div
          className="hero-text"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Badge - moved above title */}
          <motion.div 
            className="hero-badge"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            🤖 Built via Human + AI Collaboration
          </motion.div>

          {/* Title */}
          <motion.h1 
            className="hero-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            PROTECT. PURGE.
            <span className="gradient-text"> PROFIT.</span>
          </motion.h1>
          
          {/* Subtitle - UPDATED with blockchain-level messaging */}
          <motion.p 
            className="hero-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            style={{ fontSize: '1.3rem', fontWeight: 600 }}
          >
            Cleaning the Sui blockchain, one wallet at a time.
            <br />
            Remove scams, spam, and dust from your wallet.
            Reclaim 92% of your storage fees. Earn rewards.
            <br />
            Every purification makes Sui cleaner for everyone.
          </motion.p>

          {/* Hook - UPDATED */}
          <motion.p
            className="hero-hook"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{ fontSize: '1.1rem', opacity: 0.9, marginTop: '1rem' }}
          >
            <span className="gradient-text">Stop letting trash rent space in your wallet. Start purging.</span> 🔥
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            className="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <motion.button
              className="cta-button primary"
              onClick={onEnterApp}
              whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(34, 211, 238, 0.5)' }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="button-icon">⚗️</span>
              LET'S GO
            </motion.button>
            
            <motion.button
              className="cta-button secondary"
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Learn More
            </motion.button>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-visual"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <DistilleryPreview />
        </motion.div>
      </div>

      <motion.div
        className="scroll-indicator"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <span>Scroll to explore</span>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14m0 0l7-7m-7 7l-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </motion.div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SANITATION DEPARTMENT SECTION - UPDATED WITH ACTIVE BUTTON
// ═══════════════════════════════════════════════════════════════════════════

function SanitationDeptSection({ onEnterApp }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="sanitation-section" ref={ref}>
      <motion.div
        className="sanitation-content"
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.8 }}
      >
        <div className="sanitation-icon">🚽⚔️</div>
        
        <h2 className="sanitation-title">
          The Sanitation Department of Sui
        </h2>
        
        <p className="sanitation-description">
          We're not just another dApp. We're essential blockchain infrastructure.
        </p>

        <div className="sanitation-comparison">
          <div className="comparison-item">
            <div className="comparison-emoji">🏙️</div>
            <p>Like a city sanitation department keeps streets clean...</p>
          </div>
          <div className="comparison-arrow">→</div>
          <div className="comparison-item">
            <div className="comparison-emoji">🌊</div>
            <p>Distil Protocol keeps the Sui blockchain clean.</p>
          </div>
        </div>

        <div className="sanitation-benefits-grid">
          <div className="sanitation-benefit">
            <span className="benefit-emoji">🗑️</span>
            <strong>Toxic Data Removal</strong>
            <p>Spam, scams, and dust cluttering the network</p>
          </div>
          <div className="sanitation-benefit">
            <span className="benefit-emoji">🛡️</span>
            <strong>Network Health</strong>
            <p>Every purge improves Sui for everyone</p>
          </div>
          <div className="sanitation-benefit">
            <span className="benefit-emoji">🌱</span>
            <strong>Ecosystem Protection</strong>
            <p>Making Sui safer and cleaner daily</p>
          </div>
        </div>

        <p className="sanitation-tagline">
          Someone's gotta do it. Might as well earn rewards while we're at it.
        </p>

        {/* Plain text - no button styling */}
        <p className="warrior-text-plain">
          <span className="warrior-icon">🚽⚔️</span>
          Join the Toilet Warriors
        </p>
      </motion.div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TOILET MODE PARTNERSHIP SECTION - UPDATED WITH ACTIVE BUTTON
// ═══════════════════════════════════════════════════════════════════════════

function ToiletModeSection() {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const TOILET_CARDS = [
    {
      icon: '💰',
      title: 'Earn More $DISTIL',
      color: '#FFD700',
      items: [
        '+15% bonus on every purification',
        'Daily SpinWheel (up to +100 $DISTIL)',
        'FlushFrenzy speed bonuses',
        'ScratchOff instant prizes',
        '$DISTIL Gas Rebate (post-TGE)',
      ],
    },
    {
      icon: '🎨',
      title: 'Premium Experience',
      color: '#a855f7',
      items: [
        'Dark Cosmic interface theme',
        'Exclusive Toilet Mode visuals',
        'Exclusive flush animations',
        'Toilet Warrior badge & status',
      ],
    },
    {
      icon: '🏆',
      title: 'Exclusive Access',
      color: '#22d3ee',
      items: [
        'Priority feature access',
        'Exclusive competitions & tournaments',
        'FREE platform access (Dec 2026) 👀',
        'Exclusive airdrop opportunities',
        'Higher prize pools & rewards',
      ],
    },
  ];

  return (
    <section id="toilet-mode" className="toilet-mode-section" ref={ref}>
      <motion.div
        className="toilet-mode-content"
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.8 }}
      >
        <SectionHeader
          subtitle="PREMIUM TIER"
          title="🚽 Toilet Mode"
          description="Unlock exclusive earning opportunities by holding 287,000+ $TOILET tokens (~$10)"
        />

        <div className="tm-cards-grid">
          {TOILET_CARDS.map((card, i) => (
            <motion.div
              key={card.title}
              className="tm-card"
              style={{ '--tm-color': card.color }}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
            >
              <div className="tm-card-header">
                <span className="tm-card-icon">{card.icon}</span>
                <h3 className="tm-card-title">{card.title}</h3>
              </div>
              <ul className="tm-card-list">
                {card.items.map((item, j) => (
                  <li key={j} className="tm-card-item">
                    <span className="tm-arrow">→</span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <div className="tm-footer">
          <p className="tm-tagline">Hold $TOILET. Earn More. Flex Harder.</p>
          <motion.a
            href="https://flush.wtf"
            target="_blank"
            rel="noopener noreferrer"
            className="toilet-mode-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>🚽</span> Get $TOILET on flush.wtf →
          </motion.a>
          <p className="tm-cross-token">First cross-token utility on Sui</p>
        </div>
      </motion.div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HOW IT WORKS SECTION
// ═══════════════════════════════════════════════════════════════════════════

function HowItWorksSection() {
  const steps = [
    {
      number: '01',
      title: 'Connect Wallet',
      description: 'Link your Sui wallet to scan for scams, spam, and dust',
      icon: '🔗'
    },
    {
      number: '02',
      title: 'Select Items',
      description: 'Choose scams, spam, or dust to remove — with toxicity scores',
      icon: '🔍'
    },
    {
      number: '03',
      title: 'Purify',
      description: 'One click to clean everything and remove unwanted assets',
      icon: '⚗️'
    },
    {
      number: '04',
      title: 'Earn Rewards',
      description: 'Reclaim 92% storage value + earn $DISTIL + build status',
      icon: '💎'
    }
  ];

  return (
    <section id="how-it-works" className="how-it-works-section">
      <div className="section-content">
        <SectionHeader
          subtitle="THE PROCESS"
          title="How It Works"
          description="Four simple steps to purify your blockchain and earn rewards"
        />
        
        <div className="steps-grid">
          {steps.map((step, index) => (
            <StepCard key={index} step={step} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StepCard({ step, index }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      className="step-card"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ delay: index * 0.2, duration: 0.5 }}
    >
      <div className="step-number">{step.number}</div>
      <div className="step-icon">{step.icon}</div>
      <h3 className="step-title">{step.title}</h3>
      <p className="step-description">{step.description}</p>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 🛡️ SECURITY SECTION - NEW!
// ═══════════════════════════════════════════════════════════════════════════

function SecuritySection({ onEnterApp }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const securityFeatures = [
    {
      icon: '🔐',
      title: 'Non-Custodial',
      description: 'You control your keys. Always.'
    },
    {
      icon: '👁️',
      title: 'Read-Only Scanning',
      description: 'We can\'t move your funds'
    },
    {
      icon: '✅',
      title: 'Your Approval',
      description: 'You confirm every transaction'
    },
    {
      icon: '🌐',
      title: 'Open Development',
      description: 'Built transparently in public'
    }
  ];

  return (
    <section className="security-section" ref={ref}>
      <motion.div
        className="security-content"
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.6 }}
      >
        {/* Main heading */}
        <div className="security-header">
          <h2 className="security-title">
            <span className="security-icon">🛡️</span>
            Your Security Is Our Priority
          </h2>
          <p className="security-subtitle">
            Distil Protocol is designed with security at its core
          </p>
        </div>

        {/* Security features grid */}
        <div className="security-features">
          {securityFeatures.map((feature, index) => (
            <motion.div
              key={index}
              className="security-feature"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ delay: index * 0.1 + 0.2, duration: 0.5 }}
            >
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Beta warning */}
        <motion.div
          className="beta-warning"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <div className="warning-icon">⚠️</div>
          <div className="warning-content">
            <h4 className="warning-title">Early Access Beta</h4>
            <p className="warning-text">
              Professional security audit in progress (Q1 2026). 
              <br />
              Start with <strong>Demo Mode</strong> to test risk-free!
            </p>
          </div>
        </motion.div>

        {/* CTA button - centered, demo only */}
        <motion.div
          className="security-cta-centered"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <motion.button
            className="demo-mode-btn primary"
            onClick={onEnterApp}
            whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(34, 211, 238, 0.4)' }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="button-icon">🎮</span>
            Try Demo Mode
          </motion.button>
        </motion.div>
      </motion.div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// BENEFITS SECTION - UPDATED
// ═══════════════════════════════════════════════════════════════════════════

function BenefitsSection() {
  const benefits = [
    {
      icon: '💰',
      title: 'Reclaim Value',
      description: 'Recover 92% of storage fees + earn $DISTIL tokens from purification'
    },
    {
      icon: '🛡️',
      title: 'Scam Protection',
      description: 'Detect rugpulls and phishing attempts with real-time toxicity scoring'
    },
    {
      icon: '🔥',
      title: 'Deflationary Burn',
      description: '1% of every purification burned forever—making $DISTIL scarcer over time'
    },
    {
      icon: '💨',
      title: 'Dust Sweeper',
      description: 'Convert worthless small balances into value automatically'
    },
    {
      icon: '⚡',
      title: 'Earn Status',
      description: 'Build purity score and unlock elite badge tiers with exclusive benefits'
    },
    {
      icon: '🌱',
      title: 'Clean Blockchain',
      description: 'Every purification makes Sui healthier — you benefit, everyone benefits'
    }
  ];

  return (
    <section id="benefits" className="benefits-section">
      <div className="section-content">
        <SectionHeader
          subtitle="WHY DISTIL"
          title="Protocol Benefits"
          description="Join the purification movement and unlock these advantages"
        />
        
        <div className="benefits-grid">
          {benefits.map((benefit, index) => (
            <BenefitCard key={index} benefit={benefit} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BenefitCard({ benefit, index }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      className="benefit-card"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      whileHover={{ scale: 1.05, y: -5 }}
    >
      <div className="benefit-icon">{benefit.icon}</div>
      <h3 className="benefit-title">{benefit.title}</h3>
      <p className="benefit-description">{benefit.description}</p>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TOKENOMICS SECTION
// ═══════════════════════════════════════════════════════════════════════════

function TokenomicsSection() {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section id="tokenomics" className="tokenomics-section" ref={ref}>
      <div className="section-content">
        <SectionHeader
          subtitle="💎 TOKENOMICS"
          title="Designed for Long-Term Value"
          description="Multi-layered deflationary model with burns, holding bonuses, and supply-based emissions"
        />

        <div className="tokenomics-grid">
          {/* Total Supply Card */}
          <motion.div
            className="tokenomics-card highlight-card"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ delay: 0.1 }}
          >
            <div className="card-icon-large">💎</div>
            <h3>Total Supply</h3>
            <div className="big-number">100M $DISTIL</div>
            <p className="card-subtitle">Fixed forever—no inflation, ever</p>
          </motion.div>

          {/* Distribution Card */}
          <motion.div
            className="tokenomics-card"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ delay: 0.2 }}
          >
            <div className="card-icon">📊</div>
            <h3>Distribution</h3>
            <ul className="distribution-list">
              <li><span className="percent">50%</span> Fair Launch / Public Sale</li>
              <li><span className="percent">15%</span> Liquidity (LOCKED)</li>
              <li><span className="percent">20%</span> Team (30-month vest)</li>
              <li><span className="percent">10%</span> Ecosystem & Airdrops</li>
              <li><span className="percent">5%</span> Strategic Reserve</li>
            </ul>
          </motion.div>

          {/* Automatic Burns Card */}
          <motion.div
            className="tokenomics-card burn-card"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ delay: 0.3 }}
          >
            <div className="card-icon">🔥</div>
            <h3>Automatic Burns (1%)</h3>
            <p className="burn-description">Every purification burns $DISTIL permanently</p>
            <div className="split-display">
              <div className="split-item user">
                <span className="split-percent">92%</span>
                <span className="split-label">→ You</span>
              </div>
              <div className="split-item protocol">
                <span className="split-percent">7%</span>
                <span className="split-label">→ Protocol</span>
              </div>
              <div className="split-item burn">
                <span className="split-percent">1%</span>
                <span className="split-label">→ BURNED 🔥</span>
              </div>
            </div>
          </motion.div>

          {/* Holding Bonuses Card */}
          <motion.div
            className="tokenomics-card"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ delay: 0.4 }}
          >
            <div className="card-icon">💎</div>
            <h3>Holding Bonuses</h3>
            <p className="card-subtitle">No staking required—just hold!</p>
            <ul className="tier-list">
              <li><span className="tier-badge bronze">🥉</span> Bronze (1K+): <strong>+5%</strong></li>
              <li><span className="tier-badge silver">🥈</span> Silver (10K+): <strong>+10%</strong></li>
              <li><span className="tier-badge gold">🏆</span> Gold (100K+): <strong>+15%</strong></li>
              <li><span className="tier-badge diamond">💎</span> Diamond (1M+): <strong>+20%</strong> + revenue share</li>
            </ul>
          </motion.div>

          {/* Supply-Based Emissions - Full Width */}
          <motion.div
            className="tokenomics-card wide-card"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ delay: 0.5 }}
          >
            <div className="card-icon">📉</div>
            <h3>Supply-Based Emission Reduction</h3>
            <p className="emission-description">
              Rewards decrease when supply milestones hit (NOT time-based!)
            </p>
            <div className="phases-display">
              <div className="phase-item">
                <div className="phase-label">Phase 1</div>
                <div className="phase-range">0-2M emitted</div>
                <div className="phase-reward">10 $DISTIL/purge</div>
              </div>
              <div className="phase-item">
                <div className="phase-label">Phase 2</div>
                <div className="phase-range">2M-4M emitted</div>
                <div className="phase-reward">9 $DISTIL/purge</div>
              </div>
              <div className="phase-item">
                <div className="phase-label">Phase 3</div>
                <div className="phase-range">4M-6M emitted</div>
                <div className="phase-reward">8 $DISTIL/purge</div>
              </div>
              <div className="phase-item">
                <div className="phase-label">Phase 4+</div>
                <div className="phase-range">6M+ emitted</div>
                <div className="phase-reward">Continues declining</div>
              </div>
            </div>
            <p className="emission-note">
              ⚡ Self-regulating: Bull market = faster tightening, Bear = extended rewards
            </p>
          </motion.div>

          {/* Why This Matters - Full Width */}
          <motion.div
            className="tokenomics-card wide-card highlight-card"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ delay: 0.6 }}
          >
            <div className="card-icon">⚡</div>
            <h3>Why This Matters</h3>
            <div className="matters-grid">
              <div className="matter-item">
                <span className="matter-icon">✅</span>
                <div>
                  <strong>Multi-layered value:</strong> Burns + holding incentives + supply-based reduction
                </div>
              </div>
              <div className="matter-item">
                <span className="matter-icon">✅</span>
                <div>
                  <strong>First on Sui</strong> with this triple-threat combination
                </div>
              </div>
              <div className="matter-item">
                <span className="matter-icon">✅</span>
                <div>
                  <strong>Proven models:</strong> Bitcoin (scarcity) + BNB (burns) + flexible emissions
                </div>
              </div>
              <div className="matter-item">
                <span className="matter-icon">✅</span>
                <div>
                  <strong>User-aligned:</strong> More usage = more scarcity = benefits all holders
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// STATS SECTION
// ═══════════════════════════════════════════════════════════════════════════

function StatsSection() {
  const [liveStats, setLiveStats] = React.useState(null);

  React.useEffect(() => {
    getProtocolTotals().then(totals => {
      if (totals) setLiveStats(totals);
    }).catch(() => {});
  }, []);

  const stats = [
    {
      value: '100M',
      label: '$DISTIL Minted',
      icon: '💎'
    },
    {
      value: liveStats
        ? liveStats.totalObjectsCleaned.toLocaleString()
        : '—',
      label: 'Objects Purified',
      icon: '⚗️'
    },
    {
      value: liveStats
        ? liveStats.activePurifiers.toLocaleString()
        : '—',
      label: 'Active Purifiers',
      icon: '👤'
    },
    {
      value: '92%',
      label: 'Value Returned',
      icon: '💰'
    },
  ];

  return (
    <section id="stats" className="stats-section">
      <div className="section-content">
        <SectionHeader
          subtitle="BY THE NUMBERS"
          title="Protocol Stats"
          description="Real-time metrics from the Distil ecosystem"
        />
        
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <StatCard key={index} stat={stat} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCard({ stat, index }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      className="stat-card"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ delay: index * 0.15 }}
    >
      <div className="stat-icon">{stat.icon}</div>
      <motion.div 
        className="stat-value"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.5 }}
        transition={{ delay: index * 0.15 + 0.2, type: 'spring' }}
      >
        {stat.value}
      </motion.div>
      <div className="stat-label">{stat.label}</div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FAQ SECTION
// ═══════════════════════════════════════════════════════════════════════════

function FAQSection() {
  const [openIndex, setOpenIndex] = React.useState(null);

  const faqs = [
    {
      question: "What is Distil Protocol?",
      answer: "Distil is the Sanitation Department of Sui — a blockchain maintenance protocol that allows you to convert unwanted NFTs and objects into value while cleaning the network. You reclaim 92% of storage fees, earn purity points, and make Sui healthier for everyone."
    },
    {
      question: "How does purification work?",
      answer: "Simply connect your Sui wallet, select the unwanted objects you want to purify, and click Purify. The protocol destroys the objects and returns 92% of their storage value to you as SUI, while you earn purity points toward badge tiers. Every purification removes toxic data from the Sui blockchain."
    },
    {
      question: "What is Toilet Mode?",
      answer: "Toilet Mode is our premium tier for $TOILET token holders (287,000+ tokens). It unlocks the exclusive Dark Cosmic interface theme, +15% bonus rewards, exclusive mini-games, $DISTIL Gas Rebate post-TGE, and FREE access to our advanced community platform launching December 2026 with exclusive airdrop opportunities. It's the first cross-token utility partnership on Sui — $10 unlock, massive ongoing value."
    },
    {
      question: "How does the 8% fee work?",
      answer: "8% total: 7% funds protocol operations (development, security, infrastructure) + 1% is BURNED forever. This creates deflationary pressure while ensuring sustainable development. The burn is automatic and permanent—making $DISTIL scarcer with every purification."
    },
    {
      question: "What are badge tiers?",
      answer: "Badges represent your status in the protocol. Start as a Novice and progress through Purifier, Alchemist, and Master tiers by earning purity points through purifications. Higher tiers unlock exclusive benefits and recognize your contribution to keeping Sui clean."
    },
    {
      question: "Can I test without connecting a wallet?",
      answer: "Yes! Click 'Try Demo' to test the full purification flow with demo objects. No wallet needed, no gas fees."
    },
    {
      question: "Is it safe?",
      answer: "Absolutely. Our smart contracts will be audited before mainnet launch, and the protocol is built with security as the top priority. You maintain full control of your wallet at all times."
    }
  ];

  return (
    <section id="faq" className="faq-section">
      <div className="section-content">
        <SectionHeader
          subtitle="COMMON QUESTIONS"
          title="FAQ"
          description="Everything you need to know about the protocol"
        />
        
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              index={index}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQItem({ faq, index, isOpen, onClick }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      className={`faq-item ${isOpen ? 'open' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ delay: index * 0.1 }}
      onClick={onClick}
    >
      <div className="faq-question">
        <h3>{faq.question}</h3>
        <motion.span
          className="faq-icon"
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.3 }}
        >
          +
        </motion.span>
      </div>
      <motion.div
        className="faq-answer"
        initial={false}
        animate={{
          height: isOpen ? 'auto' : 0,
          opacity: isOpen ? 1 : 0
        }}
        transition={{ duration: 0.3 }}
      >
        <p>{faq.answer}</p>
      </motion.div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ROADMAP SECTION
// ═══════════════════════════════════════════════════════════════════════════

function RoadmapSection() {
  const milestones = [
    {
      phase: "Phase 1",
      title: "Foundation ✅",
      status: "Complete",
      items: [
        "Beta Launch System",
        "TGE Tracking Infrastructure",
        "Admin Monitoring Dashboard",
        "Protected Assets Transparency",
        "Whitelist Protection System",
        "Tutorial & Onboarding"
      ]
    },
    {
      phase: "Phase 2",
      title: "Private Beta & Launch 🚧",
      status: "In Progress",
      items: [
        "Whale Testing (Invite-only)",
        "2x Multiplier for Early Users",
        "$DISTIL Token Launch (TGE)",
        "Toilet Mode Partnership Live",
        "Badge & Tier System",
        "Community Building"
      ]
    },
    {
      phase: "Phase 3",
      title: "AI Security Layer 🤖",
      status: "Q3 2026",
      items: [
        "AI-Powered Scam Detection",
        "SafeMode Premium Tier",
        "Real-Time Wallet Scanning",
        "B2B Security API (Beta)",
        "Sui Guardian Integration",
        "Community Scam Database"
      ]
    },
    {
      phase: "Phase 4",
      title: "Advanced Features 🎮",
      status: "Q4 2026",
      items: [
        "On-Chain Reputation Engine",
        "Community Engagement Platform Launch 👀",
        "Exclusive Toilet Mode Benefits",
        "DeFi Partner Integrations",
        "Wallet Risk Assessment API",
        "B2B Revenue Launch"
      ]
    },
    {
      phase: "Phase 5",
      title: "Ecosystem Security 🛡️",
      status: "2027+",
      items: [
        "Wallet API Partnerships (Sui, Ethos, Suiet)",
        "Real-Time Security Integration",
        "Browser Extension (Chrome/Firefox)",
        "Cross-Chain Expansion",
        "Insurance Partnerships",
        "Industry Standard Positioning"
      ]
    }
  ];

  return (
    <section id="roadmap" className="roadmap-section">
      <div className="section-content">
        <SectionHeader
          subtitle="THE JOURNEY"
          title="Roadmap"
          description="Our vision for the future of blockchain purification"
        />
        
        <div className="roadmap-timeline">
          {milestones.map((milestone, index) => (
            <RoadmapMilestone key={index} milestone={milestone} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function RoadmapMilestone({ milestone, index }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      className="roadmap-milestone"
      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
      transition={{ delay: index * 0.2, duration: 0.6 }}
    >
      <div className={`milestone-status ${milestone.status.toLowerCase().replace(' ', '-')}`}>
        {milestone.status}
      </div>
      <div className="milestone-content">
        <div className="milestone-phase">{milestone.phase}</div>
        <h3 className="milestone-title">{milestone.title}</h3>
        <ul className="milestone-items">
          {milestone.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CTA SECTION
// ═══════════════════════════════════════════════════════════════════════════

function CTASection({ onEnterApp }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section className="cta-section" ref={ref}>
      <motion.div
        className="cta-content"
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      >
        <h2 className="cta-title">Ready to Join the Sanitation Department?</h2>
        <p className="cta-description">
          Clean the Sui blockchain and earn rewards. Every purification makes the network better.
        </p>
        <motion.button
          className="cta-button-large"
          onClick={onEnterApp}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="button-icon">🚽⚔️</span>
          Become a Toilet Warrior
        </motion.button>
      </motion.div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FOOTER - UPDATED WITH SOCIAL LINKS PLACEHOLDER
// ═══════════════════════════════════════════════════════════════════════════

function Footer({ onOpenProcessModal, onOpenBuildModal }) {
  const [openSection, setOpenSection] = React.useState(null);

  const toggle = (section) => {
    setOpenSection(prev => prev === section ? null : section);
  };

  return (
    <footer className="landing-footer compact">
      <div className="footer-container">

        {/* ── DESKTOP: Single row layout ── */}
        <div className="footer-top footer-desktop">
          {/* LEFT - Brand */}
          <div className="footer-brand">
            <div className="footer-logo">
              <img
                src="/images/distil-logo-animated.svg"
                alt="DISTIL"
                className="distil-icon"
                style={{ height: '32px', width: 'auto' }}
              />
            </div>
            <p className="footer-tagline">The Sanitation Department of Sui</p>
          </div>

          {/* CENTER-LEFT - Product links */}
          <div className="link-group">
            <h4>PRODUCT</h4>
            <a href="#how-it-works">How It Works</a>
            <a href="#benefits">Benefits</a>
            <a href="#tokenomics">Tokenomics</a>
            <a href="#toilet-mode">Toilet Mode</a>
          </div>

          {/* CENTER - AI badge + socials + legal */}
          <div className="footer-bottom">
            <div className="footer-ai-badge">
              <span className="ai-icon">🤖💚</span>
              <div className="ai-text">
                <strong>Built by Human + AI</strong>
                <span>Direct collaboration • 2026</span>
              </div>
            </div>
            <div className="footer-social">
              <a href="https://twitter.com/distilprotocol" target="_blank" rel="noopener noreferrer" className="social-link">Twitter</a>
              <a href="https://discord.gg/distilprotocol" target="_blank" rel="noopener noreferrer" className="social-link">Discord</a>
              <a href="https://t.me/distilprotocol" target="_blank" rel="noopener noreferrer" className="social-link">Telegram</a>
            </div>
            <div className="footer-legal">
              <span>© 2026 Distil Protocol</span>
              <span className="separator">•</span>
              <span>Sui Blockchain</span>
              <span className="separator">•</span>
              <span>Human vision, AI execution</span>
            </div>
          </div>

          {/* CENTER-RIGHT - Resources links */}
          <div className="link-group">
            <h4>RESOURCES</h4>
            <button onClick={onOpenProcessModal} className="footer-link-btn">Build Process</button>
            <button onClick={onOpenBuildModal} className="footer-link-btn">Build Your Own</button>
            <a href="#faq">FAQ</a>
          </div>
        </div>

        {/* ── MOBILE: Accordion layout ── */}
        <div className="footer-mobile">

          {/* Brand */}
          <div className="footer-brand footer-mobile-brand">
            <div className="footer-logo">
              <img
                src="/images/distil-logo-animated.svg"
                alt="DISTIL"
                className="distil-icon"
                style={{ height: '28px', width: 'auto' }}
              />
            </div>
            <p className="footer-tagline">The Sanitation Department of Sui</p>
          </div>

          {/* Accordion: Product */}
          <div className="footer-accordion-item">
            <button
              className="footer-accordion-header"
              onClick={() => toggle('product')}
            >
              <span>PRODUCT</span>
              <span className={`footer-accordion-chevron ${openSection === 'product' ? 'open' : ''}`}>▼</span>
            </button>
            <div className={`footer-accordion-body ${openSection === 'product' ? 'open' : ''}`}>
              <a href="#how-it-works" className="footer-accordion-link">How It Works</a>
              <a href="#benefits" className="footer-accordion-link">Benefits</a>
              <a href="#tokenomics" className="footer-accordion-link">Tokenomics</a>
              <a href="#toilet-mode" className="footer-accordion-link">Toilet Mode</a>
            </div>
          </div>

          {/* Accordion: Resources */}
          <div className="footer-accordion-item">
            <button
              className="footer-accordion-header"
              onClick={() => toggle('resources')}
            >
              <span>RESOURCES</span>
              <span className={`footer-accordion-chevron ${openSection === 'resources' ? 'open' : ''}`}>▼</span>
            </button>
            <div className={`footer-accordion-body ${openSection === 'resources' ? 'open' : ''}`}>
              <button onClick={onOpenProcessModal} className="footer-accordion-link footer-accordion-btn">Build Process</button>
              <button onClick={onOpenBuildModal} className="footer-accordion-link footer-accordion-btn">Build Your Own</button>
              <a href="#faq" className="footer-accordion-link">FAQ</a>
            </div>
          </div>

          {/* Human+AI card */}
          <div className="footer-ai-badge footer-mobile-ai">
            <span className="ai-icon">🤖💚</span>
            <div className="ai-text">
              <strong>Built by Human + AI</strong>
              <span>Direct collaboration • 2026</span>
            </div>
          </div>

          {/* Socials */}
          <div className="footer-mobile-socials">
            <a href="https://twitter.com/distilprotocol" target="_blank" rel="noopener noreferrer" className="footer-mobile-social-btn">Twitter</a>
            <a href="https://discord.gg/distilprotocol" target="_blank" rel="noopener noreferrer" className="footer-mobile-social-btn">Discord</a>
            <a href="https://t.me/distilprotocol" target="_blank" rel="noopener noreferrer" className="footer-mobile-social-btn">Telegram</a>
          </div>

          {/* Copyright */}
          <div className="footer-mobile-copyright">
            © 2026 Distil Protocol • Sui Blockchain • Human vision, AI execution
          </div>

        </div>

      </div>
    </footer>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION HEADER
// ═══════════════════════════════════════════════════════════════════════════

function SectionHeader({ subtitle, title, description }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      className="section-header"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6 }}
    >
      <h2 className="section-title-combined">
        <span className="section-subtitle-inline">{subtitle}</span>
        <span className="section-pipe"> | </span>
        <span className="section-title-inline">{title}</span>
      </h2>
      <p className="section-description">{description}</p>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DISTILLERY PREVIEW
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// FLOATING 3D OBJECTS — Phase 4
// CSS-animated geometric shapes floating around the hero visual
// Pure motion.div — no Three.js, no performance hit
// ═══════════════════════════════════════════════════════════════════════════

// ─── Floating emoji objects around the distillery ───────────────────────────
// Each item: emoji, position (% from edges), animation params
const FLOAT_ITEMS = [
  { id: 'flask',   emoji: '⚗️',  style: { top: '4%',  right: '4%'  }, dur: 6,   delay: 0,   y: [-12, 12] },
  { id: 'diamond', emoji: '💎',  style: { top: '36%', right: '2%'  }, dur: 7.5, delay: 1.2, y: [8, -18]  },
  { id: 'toilet',  emoji: '🚽',  style: { bottom: '8%',  right: '3%' }, dur: 8, delay: 0.6, y: [-10, 14] },
  { id: 'coin',    emoji: '🪙',  style: { top: '6%',  left: '3%'   }, dur: 9,   delay: 2,   y: [10, -14] },
  { id: 'sparkle', emoji: '✨',  style: { top: '46%', left: '1%'   }, dur: 5.5, delay: 0.8, y: [-8, 16]  },
  { id: 'fire',    emoji: '🔥',  style: { bottom: '12%', left: '4%' }, dur: 7,  delay: 1.8, y: [6, -12]  },
];

function DistilleryPreview() {
  return (
    <div className="dp-wrap" aria-label="Distillery pipeline illustration">

      {/* ── Floating emoji objects (absolutely positioned around the pipeline) ── */}
      <div className="dp-floaters" aria-hidden="true">
        {FLOAT_ITEMS.map(obj => (
          <motion.span
            key={obj.id}
            className="dp-float-emoji"
            style={obj.style}
            animate={{ y: obj.y, rotate: [obj.y[0] < 0 ? -6 : 6, obj.y[0] < 0 ? 6 : -6] }}
            transition={{ duration: obj.dur, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut', delay: obj.delay }}
          >
            {obj.emoji}
          </motion.span>
        ))}
        {/* Small glowing orbs */}
        <motion.span className="dp-orb dp-orb-cyan"   style={{ top: '10%', right: '28%' }} animate={{ y: [-8, 8], scale: [1, 1.3] }} transition={{ duration: 10, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut', delay: 0.4 }} />
        <motion.span className="dp-orb dp-orb-purple" style={{ bottom: '20%', left: '22%' }} animate={{ y: [10, -10], scale: [1, 1.4] }} transition={{ duration: 8.5, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut', delay: 2.5 }} />
        <motion.span className="dp-orb dp-orb-gold"   style={{ top: '28%', right: '20%' }} animate={{ y: [-6, 10], scale: [0.9, 1.3] }} transition={{ duration: 11, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut', delay: 1.5 }} />
      </div>

      {/* ── Ambient glow behind the pipeline ── */}
      <motion.div
        className="dp-ambient-glow"
        animate={{ scale: [1, 1.08, 1], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* ── THE PIPELINE ── */}
      <div className="dp-pipeline">

        {/* TOP: Toxic hopper */}
        <div className="dp-section dp-hopper-section">
          <motion.div
            className="dp-label dp-label-toxic"
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          >
            ☠️ TOXIC INPUT
          </motion.div>
          <motion.div
            className="dp-hopper"
            animate={{ opacity: [0.7, 0.9, 0.7] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="dp-hopper-inner">
              {[0, 1, 2].map(i => (
                <motion.span
                  key={i}
                  className="dp-toxic-dot"
                  animate={{ y: [0, 28, 0], opacity: [0, 0.9, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeIn', delay: i * 0.65 }}
                />
              ))}
            </div>
          </motion.div>
        </div>

        {/* PIPE: top connector */}
        <div className="dp-pipe-wrap">
          <motion.div
            className="dp-pipe"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          {[0, 1].map(i => (
            <motion.span
              key={i}
              className="dp-pipe-dot"
              animate={{ y: [0, 36], opacity: [0.8, 0] }}
              transition={{ duration: 0.9, repeat: Infinity, ease: 'easeIn', delay: i * 0.45 }}
            />
          ))}
        </div>

        {/* MIDDLE: Distillery chamber */}
        <div className="dp-section dp-chamber-section">
          <motion.div
            className="dp-chamber"
            animate={{ borderColor: ['rgba(34,211,238,0.4)', 'rgba(34,211,238,0.9)', 'rgba(34,211,238,0.4)'] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <motion.div
              className="dp-chamber-label"
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            >
              DISTIL
            </motion.div>
            <motion.div
              className="dp-swirl"
              animate={{ rotate: 360 }}
              transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
            />
          </motion.div>
        </div>

        {/* PIPE: bottom connector */}
        <div className="dp-pipe-wrap">
          <motion.div
            className="dp-pipe"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
          />
        </div>

        {/* BOTTOM: $DISTIL badge */}
        <div className="dp-section dp-badge-section">
          <div className="dp-badge-wrap">
            <motion.div
              className="dp-badge"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              <span className="dp-badge-text">$DISTIL</span>
            </motion.div>
          </div>
          <motion.div
            className="dp-label dp-label-clean"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          >
            ✨ CLEAN $DISTIL
          </motion.div>
        </div>

      </div>{/* end dp-pipeline */}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FLOATING PARTICLES
// ═══════════════════════════════════════════════════════════════════════════

function FloatingParticles() {
  // Pre-generate stable positions (avoid re-randomizing on re-render)
  const particles = React.useMemo(() => (
    [...Array(24)].map((_, i) => ({
      left: `${(i * 4.17 + (i % 3) * 7) % 100}%`,
      top: `${(i * 5.3 + (i % 5) * 9) % 100}%`,
      duration: 8 + (i % 5) * 2.5,
      delay: (i % 7) * 0.9,
      yRange: 30 + (i % 4) * 15,
    }))
  ), []);

  return (
    <div className="particles-container-landing" aria-hidden="true">
      {particles.map((p, i) => (
        <motion.div
          key={i}
          className="particle-landing"
          style={{ left: p.left, top: p.top }}
          animate={{
            y: [0, -p.yRange, 0],
            opacity: [0.15, 0.7, 0.15],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PROCESS MODAL - "How We Built This"
// ═══════════════════════════════════════════════════════════════════════════

function ProcessModal({ onClose }) {
  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>×</button>
        
        <div className="modal-header">
          <h2>🤖💙 How We Built This (A Love Story)</h2>
        </div>

        <div className="modal-body">
          <div className="story-section">
            <h3>October 2025: Steven has a problem.</h3>
            <p>His Sui wallet is 76% spam. Blockchain explorers can <em>see</em> the junk but can't <em>delete</em> it.</p>
            <p>"Someone should fix this," he thinks.</p>
            <p>Then: "Wait. <strong>I</strong> should fix this."</p>
            <p>One problem: Steven's not a 10x developer. He's just a guy with an idea and ChatGPT Plus.</p>
          </div>

          <div className="story-section">
            <h3>The Experiment:</h3>
            <p>What if instead of <em>pretending</em> AI helped (like every other crypto project), we actually <em>used</em> it?</p>
            <p>Like, for real. Full transparency. Human + AI partnership.</p>
          </div>

          <div className="story-section collaboration-grid">
            <div className="collab-column">
              <h4>Steven brings:</h4>
              <ul>
                <li>Vision (wallet cleanup should be rewarding!)</li>
                <li>Product decisions (gamification, partnerships, strategy)</li>
                <li>Testing (breaking things so you don't have to)</li>
                <li>Energy (unhealthy amounts of caffeine)</li>
              </ul>
            </div>
            <div className="collab-column">
              <h4>Claude (AI) brings:</h4>
              <ul>
                <li>Code implementation (React, Move, all the technical stuff)</li>
                <li>Optimization (making it actually work)</li>
                <li>Documentation (so we remember what we built)</li>
                <li>Patience (Steven asks a LOT of questions)</li>
              </ul>
            </div>
          </div>

          <div className="story-section timeline">
            <h3>The Timeline:</h3>
            <div className="timeline-item">Week 1: "Can we even do this?"</div>
            <div className="timeline-item">Week 4: "Holy shit, this might work."</div>
            <div className="timeline-item">Week 8: "Wait, this is actually good?"</div>
            <div className="timeline-item">Week 12: "WE'RE LAUNCHING THIS THING! 🚀"</div>
          </div>

          <div className="story-section highlight-box">
            <h3>The Result:</h3>
            <p><strong>95% feature-complete in 3 months.</strong></p>
            <p>Not because we're geniuses. Because we worked <em>together</em>.</p>
            <p>Steven didn't pretend to code everything.<br/>
            Claude didn't pretend to understand meme culture.</p>
            <p>We played to our strengths.</p>
          </div>

          <div className="story-section">
            <h3>The Philosophy:</h3>
            <p><strong>Most crypto projects in 2026:</strong> "We used AI" (they didn't)</p>
            <p><strong>Distil Protocol:</strong> "We used AI" (we really did, and we're not hiding it)</p>
            <p>Why hide it? This is the future of building.</p>
            <p>One human with vision + AI with execution = shipping actual products.</p>
            <p>No 10-person team. No $2M seed round. No waiting for "the perfect time."</p>
            <p>Just: idea → build → test → ship.</p>
          </div>

          <div className="story-section stats-grid">
            <h3>What This Means For You:</h3>
            <p>The product you're using was built by:</p>
            <div className="stat-item">✅ 1 founder (Steven)</div>
            <div className="stat-item">✅ 1 AI assistant (Claude/Anthropic)</div>
            <div className="stat-item">✅ 3 months of iteration</div>
            <div className="stat-item">✅ Countless "wait, what if we..." conversations</div>
            <div className="stat-item">✅ Zero venture capital</div>
            <div className="stat-item">✅ 100% conviction</div>
          </div>

          <div className="story-section">
            <p>Is it perfect? No.<br/>
            Does it work? Hell yes.<br/>
            Could YOU do this? Absolutely.</p>
            <p><strong>That's the point.</strong></p>
          </div>

          <div className="story-section punchline">
            <h3>The Punchline:</h3>
            <p><strong>Steven to Claude, Week 1:</strong> "Can you help me build this?"<br/>
            <strong>Claude to Steven, Week 1:</strong> "I literally cannot stop you from trying."</p>
            <p><strong>Steven to Claude, Week 12:</strong> "We fucking did it."<br/>
            <strong>Claude to Steven, Week 12:</strong> "We fucking did it."</p>
            <p className="welcome-text">Welcome to the future of building. 🤖💙</p>
          </div>

          <div className="story-section postscript">
            <p><em>P.S. - Yes, an AI helped write this page. No, that doesn't make it less true. Actually makes it MORE true. Meta, right?</em></p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// BUILD MODAL - "Build Your Own"
// ═══════════════════════════════════════════════════════════════════════════

function BuildModal({ onClose }) {
  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>×</button>
        
        <div className="modal-header">
          <h2>🚀 Build Your Own (The Blueprint)</h2>
        </div>

        <div className="modal-body">
          <div className="build-intro">
            <h3>You Don't Need Permission. You Need Process.</h3>
            <p>Distil Protocol proved something important:</p>
            <p className="highlight-text"><strong>One person + AI + conviction = shipped product.</strong></p>
            <p>No VC. No team. No "waiting for the right time."</p>
            <p>Want to build YOUR idea? Here's how:</p>
          </div>

          <div className="framework-title">
            <h3>📋 THE FRAMEWORK:</h3>
          </div>

          <div className="step-section">
            <h4>Step 1: Find a Real Problem ✅</h4>
            <p>Not "how do I make money in crypto?"<br/>
            But "what's broken that I actually encounter?"</p>
            <p>Steven's problem: "My wallet is full of spam and I can't delete it."</p>
            <p>Your problem: <strong>?</strong></p>
            <p className="test-box"><strong>The test:</strong> Would you use this even if it didn't make you money?<br/>
            If no → find a different problem.</p>
          </div>

          <div className="step-section">
            <h4>Step 2: Use AI as Your Co-Founder 🤖</h4>
            <p>You don't need to code.<br/>
            You DO need to articulate what you want.</p>
            <p><strong>Tools we used:</strong></p>
            <ul>
              <li>Claude (Anthropic) for development</li>
              <li>ChatGPT for ideation</li>
              <li>Cursor/Claude for code iteration</li>
            </ul>
            <p>Cost: $20-40/month in AI subscriptions.<br/>
            Value: Like having a senior dev on-demand.</p>
            <p><strong>The key:</strong> Treat AI like a partner, not a magic wand.</p>
            <ul>
              <li>Be specific about what you want</li>
              <li>Test everything it builds</li>
              <li>Iterate when it's not quite right</li>
              <li>Learn as you go</li>
            </ul>
          </div>

          <div className="step-section">
            <h4>Step 3: Start Small, Ship Fast 🚢</h4>
            <p>Distil didn't start as 40+ features.</p>
            <div className="progression">
              <div>Week 1: "Can we delete an object?"</div>
              <div>Week 4: "Can we make it rewarding?"</div>
              <div>Week 8: "Can we add gamification?"</div>
              <div>Week 12: "Holy shit it's complete."</div>
            </div>
            <p className="anti-advice"><strong>Anti-advice:</strong> Don't try to build everything at once.</p>
            <p>Build the core. Ship it. Iterate.</p>
            <p className="highlight-text">Perfect is the enemy of shipped.</p>
          </div>

          <div className="step-section">
            <h4>Step 4: Find Your Angle 🎯</h4>
            <p>Everyone's building on Sui/Solana/ETH/whatever.</p>
            <p>What makes YOU different?</p>
            <p><strong>Distil's angle:</strong></p>
            <ul>
              <li>First wallet cleanup tool</li>
              <li>Gamified (not boring utility)</li>
              <li>Toilet Mode (meme partnership)</li>
              <li>Human + AI transparency</li>
              <li>"Sanitation Department" branding</li>
            </ul>
            <p>Your angle: <strong>?</strong></p>
            <p className="test-box"><strong>The test:</strong> Can you explain what makes you different in one sentence?</p>
          </div>

          <div className="step-section">
            <h4>Step 5: Community → Everything 🤝</h4>
            <p>Your first 10 users matter more than your first 10,000.</p>
            <p>Distil's first users: Toilet community whales (20 people)</p>
            <p><strong>Why them?</strong></p>
            <ul>
              <li>Aligned culture (degen + utility)</li>
              <li>Engaged (will actually test)</li>
              <li>Honest (will tell us what sucks)</li>
            </ul>
            <p>Your first users: <strong>?</strong></p>
            <p className="test-box"><strong>The test:</strong> Would these people tell their friends about you?</p>
          </div>

          <div className="step-section">
            <h4>Step 6: Launch Before You're Ready 🚀</h4>
            <p>Distil is launching at 95% complete.</p>
            <p>Not 100%. Not perfect.</p>
            <p><strong>Why?</strong></p>
            <ul>
              <li>Users give better feedback than internal testing</li>
              <li>Momentum matters</li>
              <li>Done &gt; Perfect</li>
              <li>You'll never feel "ready"</li>
            </ul>
            <p className="rule-box"><strong>The rule:</strong> If you're not slightly embarrassed by your V1, you waited too long.</p>
          </div>

          <div className="step-section">
            <h4>Step 7: Build in Public 📢</h4>
            <p>Distil's approach:</p>
            <ul>
              <li>Transparent about Human + AI process</li>
              <li>Share wins AND struggles</li>
              <li>Document the journey</li>
              <li>Help others do the same</li>
            </ul>
            <p><strong>Why?</strong> Trust. Credibility. Community.</p>
          </div>

          <div className="reality-check">
            <h3>🔥 THE REALITY CHECK:</h3>
            <p><strong>This isn't easy.</strong></p>
            <p>Steven spent:</p>
            <ul>
              <li>Late nights debugging</li>
              <li>Weekends testing</li>
              <li>Moments of "this will never work"</li>
              <li>Money on tools and services</li>
            </ul>
            <p><strong>But it's POSSIBLE.</strong></p>
            <p>And that's new.</p>
            <div className="timeline-compare">
              <div>10 years ago: Impossible without team</div>
              <div>5 years ago: Possible but extremely hard</div>
              <div>Today: Hard but absolutely doable</div>
            </div>
            <p>The tools improved.<br/>
            The barrier dropped.<br/>
            Your excuse expired.</p>
          </div>

          <div className="steven-can">
            <h3>💙 IF STEVEN CAN, YOU CAN:</h3>
            <p>"I'm not a developer" → Neither was Steven<br/>
            "I don't have a team" → Neither did Steven<br/>
            "I don't have funding" → Neither did Steven<br/>
            "I don't have time" → Steven has a day job too</p>
            <p className="highlight-text"><strong>What he had:</strong> Conviction + AI + Action</p>
            <p className="highlight-text"><strong>What you need:</strong> Same thing.</p>
          </div>

          <div className="invitation">
            <h3>🎯 THE INVITATION:</h3>
            <p>Build your thing.</p>
            <p>Use these tools.</p>
            <p>Ship in 90 days.</p>
            <p>Show us what you made.</p>
            <p className="tagline">The Sanitation Department started with one person and one idea.</p>
            <p className="tagline"><strong>What will YOU start?</strong></p>
          </div>

          <div className="final-note">
            <p><em>We're not special. We're just early to showing it's possible.</em></p>
            <p><em>Now you know it's possible too.</em></p>
            <p className="your-move"><strong>Your move. 🚀</strong></p>
          </div>

          <div className="resources">
            <h4>Resources:</h4>
            <p>• anthropic.com/claude - What we used<br/>
            • docs.sui.io - If building on Sui<br/>
            • Your brain + AI = Unstoppable</p>
            <p><strong>Questions?</strong> Join the Distil Discord. We help builders.</p>
            <p>Because that's the point.</p>
            <p>💙🤖🚽💎</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default LandingPage;
